import React, {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {withReact} from "slate-react";
import {createEditor} from "slate";
import {Dialog, DialogContent} from "@material-ui/core";

import RichTextEditor from "../../../../../../../../components/RichTextEditor";
import {POPUP_LOCATIONS, POPUP_LANGS, EMPTY_SLATE, slateIsEmpty, withImages} from "../../../../../../../../utils/popups";

const T = "config.platform.customizable_popups";

// Single-popup editor. Mounted only while a popup is being added/edited (the
// parent keys it by popup id), so its four Slate editor instances are always
// fresh and seeded from the popup being edited.
const PopupEditorDialog = ({popup, saving, onCancel, onSave}) => {

    const {t} = useTranslation();

    // One stable Slate editor per language (PropertyDetails5 pattern), with image
    // (void) support so pasted image links render as <img> inside the content.
    const [editorPt] = useState(() => withImages(withReact(createEditor())));
    const [editorEn] = useState(() => withImages(withReact(createEditor())));
    const [editorEs] = useState(() => withImages(withReact(createEditor())));
    const [editorIt] = useState(() => withImages(withReact(createEditor())));
    const editors = {pt: editorPt, en: editorEn, es: editorEs, it: editorIt};

    // Seed each editor's initial value once, from the popup's stored content.
    const initialContent = useMemo(() => {
        const c = popup.content || {};
        return POPUP_LANGS.reduce((acc, lang) => {
            acc[lang] = (Array.isArray(c[lang]) && c[lang].length > 0) ? c[lang] : EMPTY_SLATE;
            return acc;
        }, {});
    }, []);

    const [name, setName] = useState(popup.name || "");
    const [active, setActive] = useState(popup.active === true);
    const [locations, setLocations] = useState(popup.locations || []);
    const [startDate, setStartDate] = useState(popup.start_date || "");
    const [endDate, setEndDate] = useState(popup.end_date || "");
    const [error, setError] = useState(null);

    const toggleLocation = (loc) => {
        setLocations(prev => prev.includes(loc) ? prev.filter(l => l !== loc) : [...prev, loc]);
        if (error) setError(null);
    };

    const handleSave = async () => {
        // Collect content straight from the live Slate documents; drop empty langs.
        const content = {};
        POPUP_LANGS.forEach(lang => {
            const value = editors[lang].children;
            if (!slateIsEmpty(value)) content[lang] = value;
        });

        if (locations.length === 0) { setError("err-popup_invalid_location"); return; }
        if (Object.keys(content).length === 0) { setError("err-popup_no_content"); return; }
        if (startDate && endDate && startDate > endDate) { setError("err-popup_invalid_dates"); return; }

        const err = await onSave({
            id: popup.id, // undefined for a new popup -> backend assigns one
            name: name.trim(),
            active,
            locations,
            start_date: startDate || null,
            end_date: endDate || null,
            content
        });
        if (err) setError(err);
    };

    return (
        <Dialog className="CustomizablePopups__dialog" open={true} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogContent className="CustomizablePopups__dialog-content">
                <h4>{popup.id ? t(`${T}.edit_popup`) : t(`${T}.new_popup`)}</h4>

                <div className="CustomizablePopups__field">
                    <label>{t(`${T}.fields.name`)}</label>
                    <input className="input_field basic_container" type="text" value={name}
                           placeholder={t(`${T}.fields.name_placeholder`)}
                           onChange={e => setName(e.target.value)}/>
                </div>

                <label className="CustomizablePopups__checkbox">
                    <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}/>
                    <span>{t(`${T}.fields.active`)}</span>
                </label>

                <div className="CustomizablePopups__field">
                    <label>{t(`${T}.fields.locations`)}</label>
                    <div className="CustomizablePopups__locations">
                        {POPUP_LOCATIONS.map(loc => (
                            <label key={loc} className="CustomizablePopups__checkbox">
                                <input type="checkbox" checked={locations.includes(loc)}
                                       onChange={() => toggleLocation(loc)}/>
                                <span>{t(`${T}.locations.${loc}`)}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="CustomizablePopups__dates">
                    <div className="CustomizablePopups__field">
                        <label>{t(`${T}.fields.start_date`)}</label>
                        <input className="input_field basic_container" type="date" value={startDate}
                               onChange={e => { setStartDate(e.target.value); if (error) setError(null); }}/>
                    </div>
                    <div className="CustomizablePopups__field">
                        <label>{t(`${T}.fields.end_date`)}</label>
                        <input className="input_field basic_container" type="date" value={endDate}
                               min={startDate || undefined}
                               onChange={e => { setEndDate(e.target.value); if (error) setError(null); }}/>
                    </div>
                </div>
                <small className="CustomizablePopups__hint">{t(`${T}.date_hint`)}</small>

                <div className="CustomizablePopups__field">
                    <label>{t(`${T}.fields.content`)}</label>
                    <div className="CustomizablePopups__editors">
                        {POPUP_LANGS.map(lang => (
                            <RichTextEditor key={lang} editor={editors[lang]} allowImages
                                            defaultValue={initialContent[lang]} lang={lang}/>
                        ))}
                    </div>
                </div>

                {error && <p className="error_message">
                    {t(`${T}.errors.${error}`, t(`${T}.errors.save_failed`))}
                </p>}

                <div className="CustomizablePopups__dialog-actions">
                    <button type="button" className="btn btn-tertiary" onClick={onCancel} disabled={saving}>
                        {t("buttons.cancel")}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {t("buttons.save")}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PopupEditorDialog;
